/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-imports */
import { Linkable } from '@/components/blocks/Linkable';
import { linkTitleSX } from '@/components/blocks/ProductDetails/styles/checkAvailabilitity';
import {
	Alert,
	Box,
	Divider,
	Fade,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import React, {
	ChangeEvent,
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { ContentContext } from '@/data/context/content';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ProductImage } from '@/components/blocks/ProductImage';
import { isKitOrBundleType } from '@/utils/productIsA';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import {
	productDetailsPriceSX,
	productDetailsPriceOfferSX,
	productDetailsPriceListSX,
} from '@/components/blocks/ProductDetails/styles/CheckAvailability';
import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import { useGeonode } from '@/data/Content/Geonode';
import { SelectWithResize } from '@/components/blocks/SelectWithResize';
import { usePhysicalStores } from '@/data/Content/GeonodeCustom';
import { SelectChangeEvent } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { useNotifications } from '@/data/Content/Notifications';
import { AdaptiveDialog } from '@/components/blocks/AdaptiveDialog';
import { OneClick } from '../OneClick';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAP_REGION, STORE_LOCATOR_LIBRARY } from '@/data/constants/storeLocator';
import { useSettings } from '@/data/Settings';
import { CheckAvailabilityState, useCheckAvailabilityState } from './hooks/CheckAvailability';
import { removeAccents } from 'integration/utils/removeAccents';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { ShowByCondition } from '../ShowByCondition';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { EMPTY_NEGATIVE, EMPTY_STRING } from '@/data/constants/marketing';
import { useStock } from '@/components/blocks/ProductDetails/hooks/useStock';

export const CheckAvailabilityDisplay: FC<{ isDpElectronico?: boolean }> = ({
	isDpElectronico,
}) => {
	const { product, selection, availability } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;
	const { someWstock } = useStock(availability);

	const openModal = useIsOpen();
	const { checkAvailabilityState, onSetLoading, onSetPartialCheckAvailabilityState } =
		useCheckAvailabilityState();

	const { showSnackbarMessage } = useNotifications();

	const handleOpenModal = () => {
		const tallaSeleccionada = selection?.attrsByIdentifier?.Talla;

		if (!tallaSeleccionada) {
			showSnackbarMessage('Selecciona una talla para ver la disponibilidad en tienda');
			return;
		}

		openModal.on();
	};
	if (!someWstock) return null;

	return (
		<React.Fragment>
			<Stack
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					'@media(min-width:960px)': {
						justifyContent: 'flex-start',
					},
				}}
			>
				<Stack>
					<StorefrontIcon
						sx={{ color: '#2682E7', fontSize: 'var(--font-size-xl)', marginRight: '2px' }}
					/>
				</Stack>
				<Linkable href="" type="inline" sx={linkTitleSX} onClick={handleOpenModal}>
					{'Ver disponibilidad en tienda física'}
				</Linkable>
			</Stack>
			<ShowByCondition condition={openModal.open}>
				<CheckAvailabilityModal
					product={product}
					selection={selection}
					checkAvailabilityState={checkAvailabilityState}
					onSetLoading={onSetLoading}
					onSetPartialCheckAvailabilityState={onSetPartialCheckAvailabilityState}
					onClose={openModal.off}
					isDpElectronico={isDpElectronico}
				/>
			</ShowByCondition>
		</React.Fragment>
	);
};

export const CheckAvailabilityModal: FC<{
	checkAvailabilityState: CheckAvailabilityState;
	product: ReturnType<typeof useProductDetails>['product'];
	selection: ReturnType<typeof useProductDetails>['selection'];
	onSetLoading: (loading: boolean) => void;
	onSetPartialCheckAvailabilityState: (state: Partial<CheckAvailabilityState>) => void;
	onClose: () => void;
	isDpElectronico?: boolean;
}> = ({
	product,
	selection,
	checkAvailabilityState,
	onSetLoading,
	onSetPartialCheckAvailabilityState,
	onClose,
	isDpElectronico,
}) => {
	const { dataStates } = useGeonode();
	const { getCities, getInventoryStores } = usePhysicalStores();

	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;
	const { offer, list, currency } = useMemo<{
		offer: number;
		list: number;
		currency: string;
		min: number;
	}>(() => {
		const { prices } = getProductDisplayInfo(sku, product);
		const defaults = { offer: 0, list: 0, currency: '' };
		return { ...defaults, ...prices, min: sku?.productPrice?.min || 0 };
	}, [sku, product]);

	const { price, priceWithoutOffer } = calculateProductPrices({
		list,
		offer,
		ribbons: sku?.ribbons ?? [],
	});

	const attributes = product?.attributes || [];
	const colorValue = findProductAttribute(attributes, 'Color1')?.value || 'Unknown';

	const { notifyError } = useNotifications();

	const handleStateChange = async (
		event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
	) => {
		const state = event.target.value;

		onSetPartialCheckAvailabilityState({
			selectedState: state,
			selectedCity: EMPTY_NEGATIVE,
			stores: [],
			loading: true,
		});

		const cities = await getCities(state);

		onSetPartialCheckAvailabilityState({
			cities: cities || [],
			loading: false,
		});
	};

	const handleSetStores = useCallback(
		async (state: string, city: string) => {
			const stores = await getInventoryStores({
				sku: selection?.sku?.partNumber || '',
				state,
				city,
				showBy: '0',
			});

			onSetPartialCheckAvailabilityState({
				stores: stores?.data || [],
				loading: false,
			});
		},
		[getInventoryStores, onSetPartialCheckAvailabilityState, selection?.sku?.partNumber]
	);

	const handleCityChange = async (
		event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
	) => {
		const city = event.target.value;

		onSetPartialCheckAvailabilityState({
			stores: [],
			selectedCity: city,
			loading: city === EMPTY_NEGATIVE ? false : true,
		});

		if (city === EMPTY_NEGATIVE) {
			return;
		}

		await handleSetStores(checkAvailabilityState.selectedState, city);
	};

	useEffect(() => {
		if (
			checkAvailabilityState.selectedState === EMPTY_STRING ||
			checkAvailabilityState.selectedCity === EMPTY_STRING ||
			checkAvailabilityState.selectedCity === EMPTY_NEGATIVE
		) {
			return;
		}

		onSetLoading(true);
		handleSetStores(checkAvailabilityState.selectedState, checkAvailabilityState.selectedCity);
	}, [
		checkAvailabilityState.selectedCity,
		checkAvailabilityState.selectedState,
		handleSetStores,
		onSetLoading,
	]);

	const { settings } = useSettings();

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: settings.mapApiKey || '',
		region: GOOGLE_MAP_REGION,
		libraries: STORE_LOCATOR_LIBRARY,
	});

	const handleCurrentLocationError = (error?: GeolocationPositionError | string) => {
		let message = 'No se pudo obtener tu ubicación actual';

		if (typeof error === 'string') {
			message = error;
		} else if (error?.code === error?.PERMISSION_DENIED) {
			message = 'Permiso denegado para obtener tu ubicación actual';
		} else if (error?.code === error?.POSITION_UNAVAILABLE) {
			message = 'Ubicación no disponible';
		} else if (error?.code === error?.TIMEOUT) {
			message = 'Tiempo de espera agotado';
		}

		notifyError(message, 'Ubicación no disponible');
		onSetLoading(false);
	};

	const handleCurrentLocation = () => {
		if (!navigator.geolocation) {
			return;
		}

		onSetLoading(true);

		navigator.geolocation.getCurrentPosition(async (position: GeolocationPosition) => {
			const geocoder = new window.google.maps.Geocoder();

			geocoder.geocode(
				{ location: { lat: position.coords.latitude, lng: position.coords.longitude } },
				async (results, status) => {
					if (!results || !results.length || status !== 'OK') {
						handleCurrentLocationError();
						return;
					}

					const addressComponents = results[0].address_components;

					const state = addressComponents.find((component) =>
						component.types.includes('administrative_area_level_1')
					)?.long_name;

					const locality = addressComponents.find((component) =>
						component.types.includes('locality')
					)?.long_name;

					if (!state || !locality) {
						handleCurrentLocationError('No se pudo obtener tu estado o ciudad');
						return;
					}

					const geoNodeState = dataStates?.GeoNode?.find((elm) =>
						elm.name?.toLowerCase().includes(state.toLowerCase())
					);

					if (!geoNodeState || !geoNodeState?.uniqueID) {
						handleCurrentLocationError('Tu estado no se encuentra en la lista de estados');
						return;
					}

					const cities = await getCities(geoNodeState.uniqueID);

					const normalizesLocality = removeAccents(locality.toLowerCase());

					const city = cities?.find((elm) =>
						removeAccents(elm.toLowerCase().trim()).includes(normalizesLocality)
					);

					if (!city) {
						handleCurrentLocationError('Tu ciudad no se encuentra en la lista de ciudades');
						return;
					}

					const stores = await getInventoryStores({
						sku: selection?.sku?.partNumber || '',
						state: geoNodeState.uniqueID,
						city,
						showBy: '0',
					});

					onSetPartialCheckAvailabilityState({
						selectedState: geoNodeState.uniqueID,
						selectedCity: city,
						cities: cities || [],
						loading: false,
						stores: stores?.data || [],
					});
				}
			);
		}, handleCurrentLocationError);
	};

	const disabledSelects = !isLoaded || checkAvailabilityState.loading;

	const isFullSelects =
		checkAvailabilityState.selectedState !== EMPTY_STRING &&
		checkAvailabilityState.selectedCity !== EMPTY_STRING &&
		checkAvailabilityState.selectedCity !== EMPTY_NEGATIVE;

	const isEmpty = !checkAvailabilityState.stores.length && !checkAvailabilityState.loading;

	const isFetching = checkAvailabilityState.loading && isFullSelects;
	const codes = product?.keyword.split(',') ?? ['', ''];

	return (
		<AdaptiveDialog title="DISPONIBILIDAD EN TIENDA" open onClose={onClose}>
			<Stack padding={'4px'}>
				<Stack display={'flex'} direction={'row'}>
					<Stack display={'flex'} direction={'column'}>
						<RenderProductImg
							fullImageRaw={product?.fullImage ?? ''}
							render={(src) => (
								<ProductImage
									sx={{ width: '128px' }}
									src={src}
									alt={'mm'}
									ribbons={[]}
									isThumbnail={true}
									isSelected={true}
								/>
							)}
						/>
					</Stack>
					<Stack display={'flex'} direction={'column'} ml={'11px'}>
						<Typography
							sx={{
								fontSize: { md: 'var(--font-size-body)', xs: '15px' },
								fontWeight: 600,
								overflow: 'hidden',
								mb: '5spx',
							}}
						>
							{product?.name}
						</Typography>
						<Typography
							sx={{
								fontSize: { md: 'var(--font-size-body)', xs: 'var(--font-size-body)' },
								fontWeight: 'normal',
								color: '#929292',
							}}
						>
							{'Código: '}
							{codes[0]}
						</Typography>
						<Stack display={'flex'} direction={'row'}>
							<Typography
								sx={{
									fontSize: { md: 'var(--font-size-body)', xs: '15px' },
									fontWeight: 600,
									mr: '11px',
								}}
							>
								{'Talla: '}
								<Box
									component={'span'}
									sx={{
										fontWeight: 'normal',
										color: '#939393',
									}}
								>
									{selection?.attrsByIdentifier?.Talla}
								</Box>
							</Typography>
							<Typography
								sx={{
									fontSize: { md: 'var(--font-size-body)', xs: '15px' },
									fontWeight: 600,
								}}
							>
								{'Color: '}
								<Box component={'span'} sx={{ fontWeight: 'normal', color: '#939393' }}>
									{colorValue}
								</Box>
							</Typography>
						</Stack>

						{isDpElectronico ? null : (
							<Stack
								direction="row"
								textAlign={'center'}
								spacing={'.3rem'}
								sx={{ marginTop: '9px' }}
							>
								{offer > 0 && offer < list ? (
									<Typography
										component="span"
										sx={{
											...productDetailsPriceListSX,
											fontSize: { md: 'var(--font-size-sm)', xs: 'var(--font-size-body)' },
										}}
										data-testid="list-price"
										id="list-price"
									>
										<PriceDisplay currency={currency} min={priceWithoutOffer} />
									</Typography>
								) : null}
								{offer > 0 && offer === list ? (
									<Typography
										component="span"
										data-testid="offer-price"
										id="offer-price"
										sx={{
											...productDetailsPriceSX,
											fontSize: { md: 'var(--font-size-sm)', xs: 'var(--font-size-body)' },
										}}
									>
										<PriceDisplay currency={currency} min={price} />
									</Typography>
								) : offer > 0 && offer < list ? (
									<Typography
										component="span"
										data-testid="offer-price"
										id="offer-price"
										sx={{
											...productDetailsPriceOfferSX,
											fontSize: { md: 'var(--font-size-sm)', xs: 'var(--font-size-body)' },
										}}
									>
										<PriceDisplay currency={currency} min={price} />
									</Typography>
								) : null}

								{offer === 0 ? (
									<Typography variant="h5" component="span" data-testid="no-price" id="no-price">
										{'Precio pendiente'}
									</Typography>
								) : null}
							</Stack>
						)}
					</Stack>
				</Stack>
				<Stack display={'flex'} direction={'row'} mt={'18px'}>
					<Typography
						sx={{
							fontSize: { md: 'var(--font-size-sm)', xs: 'var(--font-size-default)' },
							fontWeight: 'bold',
						}}
					>
						{'Seleccione la ciudad deseada para conocer la disponibilidad del producto'}
					</Typography>
				</Stack>
				<Stack display={'flex'} direction={'row'} mt={'11px'}>
					<OneClick
						wrapper="button-base"
						disabled={disabledSelects}
						onClick={handleCurrentLocation}
					>
						<LocationOnIcon sx={{ fontSize: 'var(--font-size-md)', mt: '3px', color: '#2682E7' }} />
						<Typography
							sx={{
								color: '#2682E7',
								fontSize: { md: 'var(--font-size-sm)', xs: 'var(--font-size-body)' },
								fontWeight: 'regular',
							}}
						>
							{'Utilizar mi ubicación actual'}
						</Typography>
					</OneClick>
				</Stack>
				<Grid container spacing={'18px'} mt={'6px'}>
					<Grid item md={6} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="demo-simple-select-label">{'Estado o provincia'}</InputLabel>

							<SelectWithResize
								labelId="demo-simple-select-label"
								required
								data-testid="language"
								id="language"
								name={'Estado'}
								label="Estado o provincia"
								value={checkAvailabilityState.selectedState}
								disabled={disabledSelects || !dataStates?.GeoNode?.length}
								onChange={handleStateChange}
							>
								{dataStates?.GeoNode?.map((city) => (
									<MenuItem key={city.uniqueID} value={city.uniqueID}>
										{city.Description ? city.Description[0]?.shortDescription : ''}
									</MenuItem>
								))}
							</SelectWithResize>
						</FormControl>
					</Grid>
					<Grid item md={6} xs={12} mb={{ xs: 1, md: 0 }}>
						<FormControl fullWidth>
							<InputLabel id="demo-simple-select-label-ciudad">{'Ciudad o municipio'}</InputLabel>

							<SelectWithResize
								labelId="demo-simple-select-label-ciudad"
								required
								data-testid="language"
								id="language"
								name={'Ciudad'}
								label="Ciudad o municipio"
								value={checkAvailabilityState.selectedCity}
								defaultValue={checkAvailabilityState.selectedCity}
								disabled={disabledSelects || !checkAvailabilityState.cities.length}
								onChange={handleCityChange}
							>
								<MenuItem value="-1">{'Selecciona una ciudad'}</MenuItem>
								{checkAvailabilityState?.cities?.map((city) => (
									<MenuItem key={city} value={city}>
										{city}
									</MenuItem>
								))}
							</SelectWithResize>
						</FormControl>
					</Grid>
				</Grid>
				{isFetching ? (
					<Skeleton
						variant="rectangular"
						width="100%"
						height="100px"
						sx={{ marginTop: '15px', mb: '15px' }}
					/>
				) : checkAvailabilityState.stores.length > 0 ? (
					<React.Fragment>
						<Stack display={'flex'} direction={'row'} mt={'15px'}>
							<Typography
								sx={{
									fontSize: { md: 'var(--font-size-body)', xs: 'var(--font-size-default)' },
									fontWeight: 'bold',
								}}
							>
								{'Tiendas con disponibilidad'}
							</Typography>
						</Stack>
						<Stack display={'flex'} direction={'column'} mt={'8px'}>
							{checkAvailabilityState.stores.map((store, index) => (
								<Stack
									key={index}
									display={'flex'}
									direction={'row'}
									alignItems={'center'}
									justifyContent={'space-between'}
									sx={{
										backgroundColor: index % 2 === 0 ? '#F7F7F7' : '#FFFFFF',
										height: '49px',
										px: '10px',
									}}
								>
									<Stack>
										<Linkable
											sx={{
												fontSize: {
													md: 'var(--font-size-sm)',
													xs: 'var(--font-size-default)',
												},
												fontWeight: '600',
												color: '#2C2C2C',
											}}
											target={'_blank'}
											type="inline"
											href={`https://www.google.com/maps?f=q&source=s_q&hl=es&geocode&q=${store?.latitud},${store?.longitud}&aq=&t=m&ie=UTF8&z=15&output=`}
										>
											{store.name}
										</Linkable>
									</Stack>
									<Stack>
										<Typography
											sx={{
												fontSize: {
													md: 'var(--font-size-sm)',
													xs: 'var(--font-size-default)',
												},
												fontWeight: '600',
											}}
										>
											{Number(store.quantity)}{' '}
											{Number(store.quantity) > 1 ? 'artículos' : 'artículo'}
										</Typography>
									</Stack>
								</Stack>
							))}
						</Stack>
						<Divider />
						<Stack
							display={'flex'}
							direction={'row'}
							py={'12px'}
							justifyContent={'center'}
							alignItems={'center'}
						>
							<Typography
								sx={{
									fontSize: { md: 'var(--font-size-small)', xs: 'var(--font-size-tiny)' },
									fontWeight: 'normal',
									textAlign: 'center',
								}}
							>
								{
									'El inventario puede variar del momento de la consulta al momento de concluir la compra'
								}
							</Typography>
						</Stack>
					</React.Fragment>
				) : isFullSelects && isEmpty ? (
					<Fade in>
						<Alert severity="error" sx={{ my: '15px' }}>
							{'No se encontró disponibilidad para este producto'}
						</Alert>
					</Fade>
				) : null}
			</Stack>
		</AdaptiveDialog>
	);
};
